import React, { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import Fab from '@mui/material/Fab'
import FullscreenIcon from '@mui/icons-material/Fullscreen'
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit'

/**
 * Pantalla completa.
 *
 * Pensado para cuando la app está embebida en un iframe del portal Drupal: el
 * iframe la limita al ancho de la columna de contenido y el mapa queda chico.
 *
 * Para que funcione, el iframe debe declarar allowfullscreen. Si no lo tiene,
 * requestFullscreen() es rechazado por el navegador; en ese caso abrimos la app
 * sola en otra pestaña, que logra el mismo objetivo sin dejar el botón muerto.
 */

// Safari/iOS todavía usa los prefijos webkit.
const getElementoPantallaCompleta = () =>
    document.fullscreenElement || document.webkitFullscreenElement || null

/**
 * Hook con el estado y el toggle, para poder usarlo tanto en un Fab como en
 * una acción de SpeedDial.
 *
 * @returns {{activa: boolean, alternar: function}}
 */
export const usePantallaCompleta = () => {
    const [activa, setActiva] = useState(false)

    // El usuario puede salir con Esc o F11, así que el estado se sincroniza
    // escuchando al navegador y no sólo al click.
    useEffect(() => {
        const onCambio = () => setActiva(Boolean(getElementoPantallaCompleta()))
        document.addEventListener('fullscreenchange', onCambio)
        document.addEventListener('webkitfullscreenchange', onCambio)
        onCambio()
        return () => {
            document.removeEventListener('fullscreenchange', onCambio)
            document.removeEventListener('webkitfullscreenchange', onCambio)
        }
    }, [])

    // Leaflet y Highcharts calculan su tamaño contra el contenedor. El cambio
    // de pantalla completa dispara un resize, pero llega antes de que termine
    // la transición, así que lo reemitimos.
    useEffect(() => {
        const t = setTimeout(
            () => window.dispatchEvent(new Event('resize')),
            300
        )
        return () => clearTimeout(t)
    }, [activa])

    const alternar = useCallback(async () => {
        const el = document.documentElement

        if (getElementoPantallaCompleta()) {
            const salir =
                document.exitFullscreen || document.webkitExitFullscreen
            if (salir) salir.call(document)
            return
        }

        const entrar = el.requestFullscreen || el.webkitRequestFullscreen
        try {
            if (!entrar) throw new Error('sin soporte')
            await entrar.call(el)
        } catch (_) {
            // Sin allowfullscreen en el iframe (o navegador sin soporte).
            window.open(window.location.href, '_blank', 'noopener')
        }
    }, [])

    return { activa, alternar }
}

const BotonPantallaCompleta = ({
    size = 'medium',
    color = 'primary',
    texto = true,
}) => {
    const { activa, alternar } = usePantallaCompleta()
    const etiqueta = activa ? 'Salir de pantalla completa' : 'Pantalla completa'

    return (
        <Fab
            variant={texto ? 'extended' : 'circular'}
            size={size}
            color={color}
            onClick={alternar}
            aria-label={etiqueta}
            title={etiqueta}
        >
            {activa ? (
                <FullscreenExitIcon className={texto ? 'mr-1' : ''} />
            ) : (
                <FullscreenIcon className={texto ? 'mr-1' : ''} />
            )}
            {texto && (activa ? 'Salir' : 'Pantalla completa')}
        </Fab>
    )
}

BotonPantallaCompleta.propTypes = {
    size: PropTypes.string,
    color: PropTypes.string,
    texto: PropTypes.bool,
}

export default BotonPantallaCompleta
